import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLikedStories, submitFeedback, reactStory, getStoryPDFDownloadLink } from '../../api/storyApi';
import FeedbackDialog from './FeedbackDialog';
import { useSectionVisibility } from '../../hooks/useSectionVisibility';
import { useAuth } from '../../context/AuthContext';
import LoadingOverlay from '../common/LoadingOverlay';

const FavoritesStories = ({ onLoaded, onVisible }) => {
    const [stories, setStories] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [selectedStory, setSelectedStory] = useState(null);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();
    const firstLoad = useRef(true);
    const ref = useSectionVisibility('favorites', onVisible);
    const { user } = useAuth();

    useEffect(() => {
        const fetchFavorites = async (page) => {
            setError(null);
            setLoading(true);
            try {
                const response = await getLikedStories(page);
                setStories(response.stories);
                setTotalPages(response.totalPages);
            } catch (error) {
                setError('favStories.errorFetchingStories');
            } finally {
                setLoading(false);
                if (firstLoad.current) {
                    onLoaded(1); // sectionId for Favorites = 1
                    firstLoad.current = false;
                }
            }
        };

        fetchFavorites(page);
    }, [page, onLoaded]);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(""), 10000); // 10 seconds
            return () => clearTimeout(timer);
        }
    }, [message]);

    const handleNextPage = () => {
        setPage(page + 1);
    };

    const handlePrevPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const handlePlayStory = (storySlug) => {
        navigate(`/story/${storySlug}`);
    };

    const handleLike = async (storyId) => {
        setError(null);
        try {
            await reactStory(storyId, 'like');
            updateStoryLikes(storyId, 'like');    
        } catch (error) {
            setError('publicStories.likeError');
        }
    };

    const handleDislike = async (storyId) => {
        setError(null);
        try {
            await reactStory(storyId, 'dislike');
            updateStoryLikes(storyId, 'dislike');    
        } catch (error) {
            setError('publicStories.likeError');
        }
    };

    const updateStoryLikes = (storyId, userReaction) => {
        setStories(stories.map(story => {
            if (story._id === storyId) {
                const newLikes = userReaction === 'like' ? (story.userReaction === 'like' ? story.likes : story.likes + 1) : (story.userReaction === 'like' ? story.likes - 1 : story.likes);
                const newDislikes = userReaction === 'dislike' ? (story.userReaction === 'dislike' ? story.dislikes : story.dislikes + 1) : (story.userReaction === 'dislike' ? story.dislikes - 1 : story.dislikes);
                return { ...story, likes: newLikes, dislikes: newDislikes, userReaction};
            } else return story;
        }
        ));
    };

    const openFeedbackDialog = (story) => {
        setSelectedStory(story);
    };

    const closeFeedbackDialog = () => {
        setSelectedStory(null);
    };

    const handleFeedbackSubmit = async (storyId, feedback) => {
        setError(null);
        try {
            await submitFeedback(storyId, feedback);
            setStories(stories.map(story => (story._id === storyId ? { ...story, userFeedbackText: feedback} : story)));
            setMessage(t('publicStories.feedbackSent'));
        } catch (error) {
            setError('publicStories.feedbackError');
        }
    };

    const handleDownload = async (storyId) => {
        setError(null);
        setLoading(true);
        try {
            const pdfUrlRequest = await getStoryPDFDownloadLink(storyId);
            const url = pdfUrlRequest.signedUrl;

            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = '';
            anchor.target = '_blank';
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        } catch (error) {
            if (error.response?.status === 404 && error.response?.data?.message === 'PDF not found') {
                setError('publicStories.PDFNotFound')
            } else {
                console.log(error);
                setError('publicStories.downloadError');
            }
        }
        setLoading(false);
    }

    return (
    <section id="favorites" ref={ref} className="story-section" data-bg="url('/images/bg_favorites.png')" data-filter="brightness(0.5)">
        <h2 className='story-section-h2'>❤️ {t('browseStories.favoriteStories')} ❤️</h2>
        <div className="story-section-component">

            {error && <p className='form-error'>{t(error)}</p>}

            {/* Loading screen */}
            {loading && <LoadingOverlay message={t('favStories.loadingStories')} />}

            {/* Display Message if No Stories */}
            {!loading && stories.length === 0 && (
                <div className="no-stories-message">
                    <p>{t('favStories.noStories')}</p>
                </div>
            )}

            <div style={{ display: 'flex' }}>
                <div className="pagination-controls">
                    <button className='nav-button' onClick={handlePrevPage} disabled={page === 1}><i className="fa-solid fa-arrow-left nav-button-icon"></i></button>
                </div>
                <div className="story-grid">
                    {stories.map((story) => (
                    <div key={story._id} className="story-card">
                        <div className="story-image-container">
                            <img
                                src={story.coverImage}
                                alt={story.title}
                                className="story-image"
                                onClick={() => handlePlayStory(story.slug)}
                            />
                            <div className="story-buttons-overlay">
                                <button
                                    className={`transparent-button like-btn ${story.userReaction === 'like' ? 'active' : ''}`}
                                    onClick={() => handleLike(story._id)}>
                                    👍 {story.likes || 0}
                                </button>
                                <button
                                    className={`transparent-button dislike-btn ${story.userReaction === 'dislike' ? 'active' : ''}`}
                                    onClick={() => handleDislike(story._id)}>
                                    👎 {story.dislikes || 0}
                                </button>
                                <button
                                    className={`transparent-button feedback-btn ${story.userFeedbackText ? 'active' : ''}`}
                                    onClick={() => openFeedbackDialog(story)}>
                                    💬
                                </button>
                                {user && user.subscriptionLevel !== 'free' && (
                                    <button
                                        className={`transparent-button download-btn `}
                                        onClick={() => handleDownload(story._id)}>
                                        ⏬
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="story-title">{story.title}</div>
                    </div>
                    ))}
                </div>
                <div className="pagination-controls">
                    <button className='nav-button' onClick={handleNextPage} disabled={page >= totalPages}><i className="fa-solid fa-arrow-right nav-button-icon"></i></button>
                </div>
            </div>
            {selectedStory && (
                <FeedbackDialog
                    story={selectedStory}
                    isOpen={!!selectedStory}
                    onClose={closeFeedbackDialog}
                    onSubmit={handleFeedbackSubmit}
                />
            )}
        </div>
    </section>
  );
};

export default FavoritesStories;
